/**
 * Shared U.S. date formatting — Phase G.2 (Aug 2026).
 *
 * The platform standard for user-facing date display is MM/DD/YYYY,
 * independent of the browser/OS locale. Every previous call site used
 * bare `.toLocaleDateString()` / `.toLocaleString()` which produces
 * DD/MM/YYYY on non-US locales — this module fixes that in one place.
 *
 * Storage / API values remain ISO. This is a presentation-layer helper.
 *
 * Date-only ISO strings (`YYYY-MM-DD`) are parsed by hand so a timezone
 * that is west of UTC does not shift the display back one day (a common
 * DOB / hire-date bug).
 */

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

const DATE_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const DATETIME_FMT = new Intl.DateTimeFormat("en-US", {
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

const DATE_SHORT_FMT = new Intl.DateTimeFormat("en-US", {
  year: "2-digit",
  month: "2-digit",
  day: "2-digit",
});

function _parseDateOnly(value) {
  const m = typeof value === "string" ? value.match(DATE_ONLY_RE) : null;
  if (!m) return null;
  // Build with local-time components so DOB / hire-date / move-in dates
  // never shift by one day in negative-UTC-offset zones.
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function _toDate(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  const dateOnly = _parseDateOnly(value);
  if (dateOnly) return dateOnly;
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : d;
}

/** MM/DD/YYYY (date only, U.S. order). Returns `fallback` for null/invalid. */
export function fmtDate(value, fallback = "—") {
  const d = _toDate(value);
  return d ? DATE_FMT.format(d) : fallback;
}

/** MM/DD/YYYY h:mm AM/PM (U.S. order). */
export function fmtDateTime(value, fallback = "—") {
  const d = _toDate(value);
  return d ? DATETIME_FMT.format(d) : fallback;
}

/** MM/DD/YY — for tight tabular contexts. */
export function fmtDateShort(value, fallback = "—") {
  const d = _toDate(value);
  return d ? DATE_SHORT_FMT.format(d) : fallback;
}

/**
 * v1.0.9 — format a value that MAY be a date. Strings shaped like an ISO
 * date (YYYY-MM-DD, optionally with a time suffix) render as MM/DD/YYYY;
 * everything else passes through untouched. Pure string slicing — never
 * constructs a Date, so no timezone shifting is possible.
 */
export function fmtDateLike(value, fallback = "—") {
  if (value == null || value === "") return fallback;
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[T\s]|$)/.exec(String(value));
  return m ? `${m[2]}/${m[3]}/${m[1]}` : value;
}
